/**
 * Complete Your Set - Visual Matching & Individual Add to Cart
 * Fork of Build Your Set - shows all products (in-stock + out-of-stock)
 * WCAG 2.1 Level AA Compliant
 */

(function() {
  'use strict';

  class CompleteYourSetSection {
    constructor(container) {
      this.container = container;
      this.sectionId = this.getSectionId();
      this.config = window.completeYourSetConfig?.[this.sectionId] || {};

      /* Cache DOM elements */
      this.dropdownButtons = this.container.querySelectorAll('.dropdown-button');

      /* State */
      this.selections = new Map(); /* layerId => { variantId, price, image, title, available } */
      this.activeDropdown = null;

      /* Initialize */
      this.bindEvents();
      this.restoreSelections();
    }

    getSectionId() {
      /* The wrapper itself carries the canonical section id */
      if (this.container.dataset.sectionId) return this.container.dataset.sectionId;

      const inner = this.container.querySelector('[data-section-id]');
      if (inner) return inner.dataset.sectionId;

      /* Fallback: parse from the mah-section-{id} class (ids may be non-numeric) */
      const sectionDiv = this.container.closest('[class*="mah-section-"]');
      if (sectionDiv) {
        const match = sectionDiv.className.match(/mah-section-(\S+)/);
        if (match) return match[1];
      }

      return 'unknown';
    }

    bindEvents() {
      /* Dropdown button clicks */
      this.dropdownButtons.forEach(button => {
        button.addEventListener('click', (e) => this.toggleDropdown(e, button));
        button.addEventListener('keydown', (e) => this.handleButtonKeydown(e, button));
      });

      /* Dropdown item clicks */
      this.container.querySelectorAll('.dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => this.selectItem(e, item));
        item.addEventListener('keydown', (e) => this.handleItemKeydown(e, item));
      });

      /* Dropdown search inputs */
      this.container.querySelectorAll('.dropdown-search').forEach(input => {
        input.addEventListener('input', (e) => this.filterDropdown(e, input));
        input.addEventListener('keydown', (e) => this.handleSearchKeydown(e, input));
        input.addEventListener('click', (e) => e.stopPropagation());
      });

      /* Individual add-to-cart buttons */
      this.container.querySelectorAll('.set-item-atc').forEach(button => {
        button.addEventListener('click', (e) => {
          const layerId = button.dataset.layerId;
          this.onItemAddToCart(layerId, button);
        });
      });

      /* Close dropdowns on outside click */
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target)) {
          this.closeAllDropdowns();
        }
      });

      /* Close dropdowns on Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeDropdown) {
          this.closeDropdown(this.activeDropdown);
          const button = this.activeDropdown.previousElementSibling;
          if (button && button.classList.contains('dropdown-button')) {
            button.focus();
          }
        }
      });
    }

    toggleDropdown(event, button) {
      event.stopPropagation();
      const dropdown = button.nextElementSibling;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      this.closeAllDropdowns();

      if (!isExpanded) {
        this.openDropdown(button, dropdown);
      }
    }

    openDropdown(button, dropdown) {
      button.setAttribute('aria-expanded', 'true');
      dropdown.style.display = 'block';
      this.activeDropdown = dropdown;

      /* Focus search input if present, otherwise first item */
      const search = dropdown.querySelector('.dropdown-search');
      if (search) {
        search.focus();
        const firstVisible = this.getVisibleItems(dropdown)[0];
        if (firstVisible) firstVisible.setAttribute('tabindex', '0');
      } else {
        const firstItem = dropdown.querySelector('.dropdown-item');
        if (firstItem) {
          firstItem.setAttribute('tabindex', '0');
          firstItem.focus();
        }
      }
    }

    closeDropdown(dropdown) {
      if (!dropdown) return;

      const button = dropdown.previousElementSibling;
      if (button && button.classList.contains('dropdown-button')) {
        button.setAttribute('aria-expanded', 'false');
      }

      dropdown.style.display = 'none';

      dropdown.querySelectorAll('.dropdown-item').forEach(item => {
        item.setAttribute('tabindex', '-1');
      });

      /* Clear search filter on close */
      const search = dropdown.querySelector('.dropdown-search');
      if (search && search.value) {
        search.value = '';
        this.applyFilter(dropdown, '');
      }

      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null;
      }
    }

    getVisibleItems(dropdown) {
      return Array.from(dropdown.querySelectorAll('.dropdown-item')).filter(item => {
        return item.style.display !== 'none';
      });
    }

    filterDropdown(event, input) {
      const dropdown = input.closest('.dropdown-options');
      this.applyFilter(dropdown, input.value);
    }

    applyFilter(dropdown, query) {
      const q = query.trim().toLowerCase();
      const items = dropdown.querySelectorAll('.dropdown-item');
      let visibleCount = 0;

      items.forEach(item => {
        const title = (item.dataset.productTitle || '').toLowerCase();
        const match = !q || title.includes(q);
        item.style.display = match ? '' : 'none';
        item.setAttribute('tabindex', '-1');
        if (match) visibleCount++;
      });

      /* Set tabindex on first visible item for keyboard flow */
      const firstVisible = this.getVisibleItems(dropdown)[0];
      if (firstVisible) firstVisible.setAttribute('tabindex', '0');

      /* Show/hide "no results" message */
      let noResults = dropdown.querySelector('.dropdown-no-results');
      if (visibleCount === 0) {
        if (!noResults) {
          noResults = document.createElement('div');
          noResults.className = 'dropdown-no-results';
          noResults.textContent = 'No products found';
          dropdown.appendChild(noResults);
        }
        noResults.style.display = '';
      } else if (noResults) {
        noResults.style.display = 'none';
      }
    }

    handleSearchKeydown(event, input) {
      const dropdown = input.closest('.dropdown-options');

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const firstVisible = this.getVisibleItems(dropdown)[0];
          if (firstVisible) this.focusItem(firstVisible);
          break;
        }
        case 'Enter': {
          event.preventDefault();
          const firstVisible = this.getVisibleItems(dropdown)[0];
          if (firstVisible) this.selectItem(event, firstVisible);
          break;
        }
        case 'Escape': {
          event.preventDefault();
          this.closeDropdown(dropdown);
          const button = dropdown.previousElementSibling;
          if (button && button.classList.contains('dropdown-button')) {
            button.focus();
          }
          break;
        }
      }
    }

    closeAllDropdowns() {
      this.container.querySelectorAll('.dropdown-options').forEach(dropdown => {
        this.closeDropdown(dropdown);
      });
    }

    handleButtonKeydown(event, button) {
      const dropdown = button.nextElementSibling;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      switch(event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.toggleDropdown(event, button);
          break;

        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          if (!isExpanded) {
            this.openDropdown(button, dropdown);
          }
          break;
      }
    }

    handleItemKeydown(event, item) {
      const dropdown = item.closest('.dropdown-options');
      const items = this.getVisibleItems(dropdown);
      const currentIndex = items.indexOf(item);

      switch(event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.selectItem(event, item);
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.focusNextItem(items, currentIndex, 1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (currentIndex === 0) {
            const search = dropdown.querySelector('.dropdown-search');
            if (search) {
              search.focus();
              break;
            }
          }
          this.focusNextItem(items, currentIndex, -1);
          break;

        case 'Home':
          event.preventDefault();
          this.focusItem(items[0]);
          break;

        case 'End':
          event.preventDefault();
          this.focusItem(items[items.length - 1]);
          break;

        case 'Tab':
          this.closeDropdown(dropdown);
          break;
      }
    }

    focusNextItem(items, currentIndex, direction) {
      let nextIndex = currentIndex + direction;

      if (nextIndex < 0) {
        nextIndex = items.length - 1;
      } else if (nextIndex >= items.length) {
        nextIndex = 0;
      }

      this.focusItem(items[nextIndex]);
    }

    focusItem(item) {
      const dropdown = item.closest('.dropdown-options');
      dropdown.querySelectorAll('.dropdown-item').forEach(i => {
        i.setAttribute('tabindex', '-1');
        i.setAttribute('aria-selected', 'false');
      });

      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-selected', 'true');
      item.focus();
    }

    selectItem(event, item) {
      event.stopPropagation();

      const variantId = item.dataset.variantId;
      const price = parseFloat(item.dataset.price);
      const image = item.dataset.layerImage;
      const title = item.dataset.productTitle;
      const available = item.dataset.available === 'true';
      const hasVariants = item.dataset.hasVariants === 'true';
      const variantsData = item.dataset.variants;

      const dropdown = item.closest('.dropdown-options');
      const button = dropdown.previousElementSibling;
      const layerId = button.dataset.layerId;

      /* Update button text */
      const buttonText = button.querySelector('.dropdown-button-text');
      if (buttonText) {
        buttonText.textContent = title;
      }

      /* Handle variant selector */
      if (hasVariants && variantsData) {
        this.showVariantSelector(layerId, variantsData, variantId, image);
      } else {
        this.hideVariantSelector(layerId);
        this.storeSelection(layerId, variantId, price, image, title, available);
      }

      this.closeDropdown(dropdown);
      button.focus();
    }

    showVariantSelector(layerId, variantsJson, defaultVariantId, fallbackImage) {
      const selectorId = layerId === 'primary' ? `primary-${this.sectionId}` : layerId;
      const variantSelector = this.container.querySelector(`#variant-selector-${selectorId}`);
      const variantSelect = this.container.querySelector(`#variant-select-${selectorId}`);

      if (!variantSelector || !variantSelect) return;

      /* Product-level image used when a variant has no image of its own */
      variantSelect.dataset.fallbackImage = fallbackImage || '';

      try {
        const variants = JSON.parse(variantsJson);

        variantSelect.innerHTML = '';

        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.dataset.price = variant.price;
          option.dataset.available = variant.available;

          /* Out-of-stock variants stay selectable so customers can visualize items they already own;
             the per-item action row handles blocking add-to-cart for unavailable variants */
          if (variant.available) {
            option.textContent = variant.title;
          } else {
            option.textContent = variant.title + ' - Out of Stock';
          }

          /* Image priority: variant metafield > variant featured > null (falls back to product) */
          let variantImage = null;
          if (variant.build_your_set_image && variant.build_your_set_image.src) {
            variantImage = variant.build_your_set_image.src;
          } else if (variant.featured_image && variant.featured_image.src) {
            variantImage = variant.featured_image.src;
          }

          option.dataset.image = variantImage || '';
          if (variantImage) {
            option.dataset.variantImage = variantImage;
          }

          if (variant.id == defaultVariantId) {
            option.selected = true;
          }
          variantSelect.appendChild(option);
        });

        variantSelector.style.display = 'block';

        /* Use onchange (not addEventListener) so re-selecting a product doesn't stack handlers */
        variantSelect.onchange = (e) => this.onVariantChange(e, layerId);

        /* Trigger initial selection */
        this.onVariantChange({ target: variantSelect }, layerId);

      } catch (error) {
        console.error('Error parsing variants:', error);
        this.hideVariantSelector(layerId);
      }
    }

    hideVariantSelector(layerId) {
      const selectorId = layerId === 'primary' ? `primary-${this.sectionId}` : layerId;
      const variantSelector = this.container.querySelector(`#variant-selector-${selectorId}`);
      if (variantSelector) {
        variantSelector.style.display = 'none';
      }
    }

    onVariantChange(event, layerId) {
      const select = event.target;
      const selectedOption = select.options[select.selectedIndex];

      const variantId = selectedOption.value;
      const price = parseFloat(selectedOption.dataset.price);
      const available = selectedOption.dataset.available === 'true';
      /* Variant image, else fall back to the product-level layer image (e.g. out-of-stock variants) */
      const image = selectedOption.dataset.variantImage || selectedOption.dataset.image || select.dataset.fallbackImage || null;

      const button = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`);
      const buttonText = button?.querySelector('.dropdown-button-text');
      const productTitle = buttonText?.textContent || '';
      const fullTitle = `${productTitle} - ${selectedOption.textContent}`;

      this.storeSelection(layerId, variantId, price, image, fullTitle, available);
    }

    storeSelection(layerId, variantId, price, image, title, available) {
      this.selections.set(layerId, { variantId, price, image, title, available });

      this.saveSelections();

      /* Update canvas preview regardless of availability */
      if (image) {
        this.updateCanvasLayer(layerId, image, title);
      }

      /* Update per-item action row */
      this.updateItemAction(layerId, price, available);
    }

    saveSelections() {
      const storageKey = `completeYourSet_${this.sectionId}`;
      const data = Array.from(this.selections.entries());
      sessionStorage.setItem(storageKey, JSON.stringify(data));
    }

    restoreSelections() {
      const storageKey = `completeYourSet_${this.sectionId}`;
      const saved = sessionStorage.getItem(storageKey);

      if (!saved) return;

      try {
        const data = JSON.parse(saved);

        data.forEach(([layerId, selection]) => {
          const dropdown = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`)?.nextElementSibling;
          if (!dropdown) return;

          const item = dropdown.querySelector(`[data-variant-id="${selection.variantId}"]`);
          if (!item) return;

          const button = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`);
          const buttonText = button?.querySelector('.dropdown-button-text');
          if (buttonText) {
            buttonText.textContent = selection.title;
          }

          this.selections.set(layerId, selection);

          if (selection.image) {
            this.updateCanvasLayer(layerId, selection.image, selection.title);
          }

          this.updateItemAction(layerId, selection.price, selection.available);
        });

      } catch (error) {
        console.error('Error restoring selections:', error);
      }
    }

    updateCanvasLayer(layerId, imageUrl, altText) {
      const layer = this.container.querySelector(`[data-layer-id="${layerId}"]`);
      if (!layer) return;

      let img = layer.querySelector('img.layer-image');

      if (img) {
        img.src = imageUrl;
        img.alt = altText;
      } else {
        const placeholder = layer.querySelector('.layer-placeholder');
        if (placeholder) {
          placeholder.style.display = 'none';
        }

        img = document.createElement('img');
        img.src = imageUrl;
        img.alt = altText;
        img.className = 'layer-image';
        img.loading = 'lazy';
        layer.appendChild(img);
      }
    }

    updateItemAction(layerId, price, available) {
      const actionRow = this.container.querySelector(`.set-item-action[data-layer-id="${layerId}"]`);
      if (!actionRow) return;

      actionRow.style.display = 'flex';

      const priceEl = actionRow.querySelector('.set-item-price');
      const atcButton = actionRow.querySelector('.set-item-atc');
      const soldOutEl = actionRow.querySelector('.set-item-sold-out');

      if (available) {
        const formattedPrice = '$' + (price / 100).toFixed(2);
        priceEl.textContent = formattedPrice;
        priceEl.parentElement.style.display = '';
        atcButton.style.display = '';
        atcButton.disabled = false;
        soldOutEl.style.display = 'none';
      } else {
        priceEl.parentElement.style.display = 'none';
        atcButton.style.display = 'none';
        soldOutEl.style.display = '';
      }
    }

    onItemAddToCart(layerId, button) {
      const selection = this.selections.get(layerId);
      if (!selection || !selection.available) return;

      if (button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('items[][id]', selection.variantId);
      formData.append('items[][quantity]', 1);

      const cart = document.querySelector('cart-drawer');
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      let hadError = false;

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            alert(response.description || 'Error adding to cart');
            hadError = true;
            return;
          } else if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          cart.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
          alert('Error adding to cart');
          hadError = true;
        })
        .finally(() => {
          button.classList.remove('loading');
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (!hadError) button.removeAttribute('aria-disabled');
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
    }
  }

  function init() {
    const sections = document.querySelectorAll('.complete-your-set-wrapper');
    sections.forEach(section => {
      new CompleteYourSetSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Reinitialize on Customizer section reload */
  document.addEventListener('shopify:section:load', function(event) {
    setTimeout(() => {
      init();
    }, 100);
  });

})();
